import { correlationService } from '@app/api';
export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

export interface LogContext {
  [key: string]: any;
  userId?: string | number;
  sessionId?: string;
  correlationId?: string | null;
  url?: string;
  method?: string;
  statusCode?: number;
  requestId?: string;
  stackTrace?: string;
}

export interface StructuredLogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: LogContext;
}

class Logger {
  private formatLogEntry(level: LogLevel, message: string, context?: LogContext): StructuredLogEntry {
    let correlationId: string | null = null;
    try {
      correlationId = correlationService.get();
    } catch (error) {
      // Gracefully handle correlation service failures
      // Don't log here to avoid infinite recursion
    }

    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: {
        ...context,
        correlationId,
      },
    };
  }

  private isDevelopment(): boolean {
    // Check for test environment first
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
      return true;
    }
    // Check for development environment
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      return true;
    }
    // For Vite, we need to check differently to avoid import.meta issues in Jest
    // This will be replaced at build time by Vite
    return false;
  }

  private shouldLog(level: LogLevel): boolean {
    // Always log errors, warnings in production
    if (level === LogLevel.ERROR || level === LogLevel.WARN) {
      return true;
    }
    // Log debug and info only in development
    return this.isDevelopment();
  }

  private logToConsole(entry: StructuredLogEntry): void {
    const { timestamp, level, message, context } = entry;
    const formattedMessage = `[${timestamp}] [${level}] ${message}`;

    switch (level) {
      case LogLevel.DEBUG:
        console.debug(formattedMessage, context || '');
        break;
      case LogLevel.INFO:
        console.info(formattedMessage, context || '');
        break;
      case LogLevel.WARN:
        console.warn(formattedMessage, context || '');
        break;
      case LogLevel.ERROR:
        console.error(formattedMessage, context || '');
        break;
    }
  }

  public log(message: string, context?: LogContext): void {
    this.info(message, context);
  }

  public debug(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      const entry = this.formatLogEntry(LogLevel.DEBUG, message, context);
      this.logToConsole(entry);
    }
  }

  public info(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.INFO)) {
      const entry = this.formatLogEntry(LogLevel.INFO, message, context);
      this.logToConsole(entry);
    }
  }

  public warn(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.WARN)) {
      const entry = this.formatLogEntry(LogLevel.WARN, message, context);
      this.logToConsole(entry);
    }
  }

  public error(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      // Capture stack trace if not provided
      if (!context?.stackTrace) {
        const error = new Error();
        context = {
          ...context,
          stackTrace: error.stack,
        };
      }
      const entry = this.formatLogEntry(LogLevel.ERROR, message, context);
      this.logToConsole(entry);
    }
  }

  public logApiError(message: string, error: unknown, requestDetails?: { url: string; method: string }): void {
    const context: LogContext = {
      ...requestDetails,
    };

    if (error instanceof Error) {
      context.stackTrace = error.stack;
      context.errorMessage = error.message;
      context.errorName = error.name;
    } else if (typeof error === 'object' && error !== null) {
      context.error = JSON.stringify(error);
    } else {
      context.error = String(error);
    }

    this.error(message, context);
  }
}

const logger = new Logger();

export default logger;
